import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card } from '../components/ui/Card';
import { Plus, X, Building, ChevronRight, Users, DollarSign, BarChart, LogOut, User, Settings, Zap, Check, Star, Rocket, Shield, Headphones, Globe, UserCheck, CreditCard, ArrowRight, ArrowLeft, AlertTriangle, Eye, EyeOff, CheckCircle, Lock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { kisitliTip } from '../components/RestrictedSidebar';

const API_URL = 'https://api.glowytics.com/api';
const DEMO_BRAND_ID = 'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4';

// Extracted outside component to avoid re-creation on every render
const MONTHS = Array.from({ length: 12 }, (_, i) => {
  const m = (i + 1).toString().padStart(2, '0');
  return { value: m, label: m };
});

const YEARS = Array.from({ length: 10 }, (_, i) => {
  const y = new Date().getFullYear() + i;
  const yy = y.toString().slice(-2);
  return { value: yy, label: yy };
});

const formatFeature = (feature) =>
  feature
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

// Faster step transition: CSS-based translate, shorter duration, no spring
const stepVariants = {
  hidden: (direction) => ({
    opacity: 0,
    x: direction > 0 ? '30%' : '-30%'
  }),
  visible: {
    opacity: 1,
    x: '0%',
    transition: {
      duration: 0.25,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  },
  exit: (direction) => ({
    opacity: 0,
    x: direction < 0 ? '30%' : '-30%',
    transition: {
      duration: 0.2,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  })
};

const StepComponent = React.memo(({ children, direction }) => (
  <motion.div
    custom={direction}
    variants={stepVariants}
    initial="hidden"
    animate="visible"
    exit="exit"
    style={{ willChange: 'transform, opacity' }}
    className="w-full"
  >
    {children}
  </motion.div>
));

// Örnek marka verileri
const mockBrands = [
  {
    id: 1,
    name: "E-Commerce Store",
    type: "Online Retail",
    stats: {
      campaigns: 12,
      monthlySpend: "$5,400",
      activeAds: 45
    }
  },
  {
    id: 2,
    name: "Fashion Boutique",
    type: "Retail",
    stats: {
      campaigns: 8,
      monthlySpend: "$3,200",
      activeAds: 28
    }
  },
  {
    id: 3,
    name: "Tech Solutions",
    type: "SaaS",
    stats: {
      campaigns: 15,
      monthlySpend: "$8,900",
      activeAds: 62
    }
  }
];

export default function BrandSelector() {
  const navigate = useNavigate();
  const { setSelectedBrand, logout, user } = useAuth();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newBrandName, setNewBrandName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [industry, setIndustry] = useState('');
  const [timeZone, setTimeZone] = useState('');
  const [currency, setCurrency] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: []
  });
  const [isBillingModalOpen, setIsBillingModalOpen] = useState(false);
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [subscriptionToCancel, setSubscriptionToCancel] = useState(null);
  const [billingActiveTab, setBillingActiveTab] = useState('subscriptions'); // 'subscriptions', 'cards', 'history'
  
  // PayTR Modal States  
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [currentBrandPaying, setCurrentBrandPaying] = useState(null);
  
  // Saved Cards States
  const [storedCards, setStoredCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(false);
  
  // New Card Payment States - PayTR Direct API için
  const [isNewCardModalOpen, setIsNewCardModalOpen] = useState(false);
  const [cardDetails, setCardDetails] = useState({
    cc_owner: '',
    card_number: '',
    expiry_month: '',
    expiry_year: '',
    cvv: '',
    store_card: true
  });
  const [paymentData, setPaymentData] = useState(null);
  const [isProcessingNewCard, setIsProcessingNewCard] = useState(false);
  
  // Promo Code States
  const [isPromoOpen, setIsPromoOpen] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoDiscount, setPromoDiscount] = useState(null);
  
  // Exchange Rate State
  const [exchangeRate, setExchangeRate] = useState(null);
  
  // Multi-step modal states
  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);

  const [cardToDelete, setCardToDelete] = useState(null);
  const [subscriptionToRenew, setSubscriptionToRenew] = useState(null);
  const [isRenewModalOpen, setIsRenewModalOpen] = useState(false);

  useEffect(() => {
    fetchBrands();
    fetchSubscriptionPlans();
    fetchExchangeRate();
  }, []);
  
  // Exchange rate fetch (sayfa yüklendiğinde)
  const fetchExchangeRate = async () => {
    try {
      const response = await fetch(`${API_URL}/payment/exchange-rate`);
      const data = await response.json();
      if (data.success) {
        setExchangeRate(data.rate);
        console.log(`💱 Exchange rate loaded: 1 USD = ${data.rate} TRY`);
      }
    } catch (error) {
      console.error('Exchange rate fetch error:', error);
      setExchangeRate(35.50);
    }
  };

  // Password strength checker
  const checkPasswordStrength = (password) => {
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    };

    const score = Object.values(checks).filter(Boolean).length;
    const feedback = [];

    if (!checks.length) feedback.push('At least 8 characters');
    if (!checks.uppercase) feedback.push('One uppercase letter');
    if (!checks.lowercase) feedback.push('One lowercase letter');
    if (!checks.number) feedback.push('One number');
    if (!checks.special) feedback.push('One special character');

    return { score, feedback, checks };
  };

  // Update password strength when new password changes
  useEffect(() => {
    if (newPassword) {
      setPasswordStrength(checkPasswordStrength(newPassword));
    } else {
      setPasswordStrength({ score: 0, feedback: [] });
    }
  }, [newPassword]);

  const fetchSubscriptionPlans = async () => {
    try {
      const response = await fetch(`${API_URL}/subscription/plans`, {
        credentials: 'include'
      });
      const data = await response.json();
      setSubscriptionPlans(data.plans || []);
    } catch (error) {
      console.error('Fetch subscription plans error:', error);
      // Fallback plans if API fails
      setSubscriptionPlans([
        {
          id: 'standard',
          name: 'standard',
          displayName: 'Standard',
          description: 'For individuals and small teams getting started.',
          priceMonthly: 199.00,
          maxTeamMembers: 1,
          aiCreditsMonthly: 0,
          features: [
            'All Platform Integrations',
            'Automated Reporting', 
            'Task & Workflow Management',
            'Mobile App Access',
            'Basic AI Assistant'
          ]
        },
        {
          id: 'pro',
          name: 'pro',
          displayName: 'Pro',
          description: 'For growing teams that need more power and collaboration.',
          priceMonthly: 349.00,
          maxTeamMembers: 3,
          aiCreditsMonthly: 100,
          features: [
            'All Standard Features',
            '100 GlowyAI Credits / Month',
            'Task & Workflow Management', 
            'Performance Notifications',
            'Priority Support'
          ]
        },
        {
          id: 'enterprise',
          name: 'enterprise',
          displayName: 'Enterprise',
          description: 'For large organizations with custom needs.',
          priceMonthly: 0,
          customPrice: "Contact Sales",
          maxTeamMembers: -1,
          aiCreditsMonthly: -1,
          features: [
            'All Pro Features',
            'Unlimited GlowyAI Credits',
            'Custom Integrations',
            'Website Integration',
            'Dedicated Account Manager'
          ]
        }
      ]);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await fetch(`${API_URL}/brands`, {
        credentials: 'include'
      });
      const data = await response.json();
      setBrands(data.brands || []);
      setLoading(false);
    } catch (error) {
      console.error('Fetch brands error:', error);
      setLoading(false);
    }
  };

  const handleCreateBrand = async (e) => {
    if (e) e.preventDefault();
    
    // Validate required fields
    if (!newBrandName.trim()) {
      toast.error('Please enter brand name');
      return;
    }
    if (!selectedPlan) {
      toast.error('Please select a plan');
      return;
    }

    setIsCreating(true);
    try {
      // Create brand with subscription
      const response = await fetch(`${API_URL}/brands`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          name: newBrandName,
          websiteUrl,
          industry,
          timeZone,
          currency,
          logoUrl,
          // Include subscription info
          planId: selectedPlan.id,
          startTrial: true
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create brand');
      }

      const data = await response.json();
      
      // Brand listesini güncelle
      setBrands(prevBrands => [...prevBrands, data.brand]);
      
      // Modal'ı kapat ve formu temizle
      setIsModalOpen(false);
      setCurrentStep(1);
      setSelectedPlan(null);
      setNewBrandName('');
      setWebsiteUrl('');
      setIndustry('');
      setTimeZone('');
      setCurrency('');
      setLogoUrl('');
      
      toast.success(`🎉 ${selectedPlan.displayName} trial started! Brand created successfully.`);

      // Yeni brand'i otomatik seç ve dashboard'a yönlendir
      await setSelectedBrand(data.brand);
      localStorage.setItem('selectedBrand', JSON.stringify(data.brand));
      localStorage.setItem('currentBrandId', data.brand.id);
      navigate('/overview');

    } catch (error) {
      console.error('Create brand error:', error);
      toast.error(error.message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleBrandSelect = async (brandId) => {
    try {
      await setSelectedBrand(brands.find(b => b.id === brandId));
      localStorage.setItem('selectedBrand', JSON.stringify(brands.find(b => b.id === brandId)));
      localStorage.setItem('currentBrandId', brandId);

      // Kısıtlı rol (FINANCE / GBP) → doğrudan kendi sayfası,
      // BRANDING → Market Insights, diğerleri Overview.
      const secilen = brands.find(b => b.id === brandId);
      const kisit = kisitliTip(secilen);
      const role = secilen?.currentUserRole;
      navigate(
        kisit ? kisit.yol
          : role === 'BRANDING' ? '/market-insights/facebook-ads-library'
          : '/overview'
      );

    } catch (error) {
      console.error('Error selecting brand:', error);
      toast.error('Failed to select brand');
    }
  };

  const handleCreateNew = useCallback(() => {
    setCurrentStep(1);
    setSelectedPlan(null);
    setIsModalOpen(true);
  }, []);

  // Profile modal'ını açma fonksiyonu
  const handleOpenProfileModal = () => {
    setIsProfileModalOpen(true);
    setIsUserMenuOpen(false);  // Dropdown'ı kapatalım
  };

  // Billing modal'ını açma fonksiyonu
  const handleOpenBillingModal = async () => {
    setIsBillingModalOpen(true);
    setIsUserMenuOpen(false);
    
    // Brands'lerden subscription bilgilerini çıkar (demo brand hariç)
    const subscriptionsFromBrands = brands
      .filter(brand => brand.subscription && brand.id !== DEMO_BRAND_ID)
      .map(brand => {
        // Plan fiyatını plan adına göre belirle
        let planPrice = 199; // Default Standard
        if (brand.subscription.planName === 'Pro') {
          planPrice = 349;
        } else if (brand.subscription.planName === 'Enterprise') {
          planPrice = 999;
        }
        
        return {
          id: brand.subscription.id,
          status: brand.subscription.status,
          trialEndsAt: brand.subscription.trialEndsAt,
          currentPeriodEnd: brand.subscription.currentPeriodEnd,
          aiCreditsRemaining: brand.subscription.aiCreditsRemaining,
          plan: {
            id: brand.subscription.planId,
            displayName: brand.subscription.planName,
            priceMonthly: planPrice
          },
          brand: {
            id: brand.id,
            name: brand.name,
            industry: brand.industry
          }
        };
      });
    
    setUserSubscriptions(subscriptionsFromBrands);
    
    // Payment history'yi fetch et
    try {
      const response = await fetch(`${API_URL}/payment/history`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setPaymentHistory(data.paymentHistory || []);
      } else {
        console.error('Failed to fetch payment history');
        setPaymentHistory([]);
      }
    } catch (error) {
      console.error('Payment history fetch error:', error);
      setPaymentHistory([]);
    }
    
    // Saved cards'ları fetch et
    await fetchStoredCards();
  };

  // Kayıtlı kartları fetch et
  const fetchStoredCards = async () => {
    setLoadingCards(true);
    try {
      const response = await fetch(`${API_URL}/payment/cards`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setStoredCards(data.cards || []);
      } else {
        console.error('Failed to fetch stored cards');
        setStoredCards([]);
      }
    } catch (error) {
      console.error('Fetch stored cards error:', error);
      setStoredCards([]);
    } finally {
      setLoadingCards(false);
    }
  };

  // Kart silme işlemini başlatır, modal'ı tetikler
  const handleDeleteCard = (ctoken) => {
    const card = storedCards.find(c => c.ctoken === ctoken);
    if (card) {
        setCardToDelete(card);
    }
  };

  // Asıl silme işlemini gerçekleştirir
  const confirmDeleteCard = async () => {
    if (!cardToDelete) return;

    try {
        const response = await fetch(`${API_URL}/payment/cards/${cardToDelete.ctoken}`, {
            method: 'DELETE',
            credentials: 'include'
        });

        if (response.ok) {
            toast.success('Card deleted successfully');
            await fetchStoredCards(); // Kartları yenile
        } else {
            const data = await response.json();
            throw new Error(data.error || 'Failed to delete card');
        }
    } catch (error) {
        console.error('Delete card error:', error);
        toast.error(error.message);
    } finally {
        setCardToDelete(null); // Modalı kapat
    }
  };

  // Subscription iptal etme modal açma
  const handleCancelSubscription = (subscriptionId, brandName) => {
    setSubscriptionToCancel({ subscriptionId, brandName });
    setIsCancelModalOpen(true);
  };

  // Subscription iptal etme onayı
  const handleConfirmCancelSubscription = async () => {
    if (!subscriptionToCancel) return;

    try {
      // 1. Subscription'ı iptal et
      const cancelResponse = await fetch(`${API_URL}/brands/subscription/${subscriptionToCancel.subscriptionId}/cancel`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!cancelResponse.ok) {
        const error = await cancelResponse.json();
        throw new Error(error.error || 'Failed to cancel subscription');
      }

      // 2. Brand'i de sil (subscription olmayınca brand'in anlamı yok)
      const brandToDelete = userSubscriptions.find(sub => sub.id === subscriptionToCancel.subscriptionId);
      if (brandToDelete) {
        const deleteResponse = await fetch(`${API_URL}/brands/${brandToDelete.brand.id}`, {
          method: 'DELETE',
          credentials: 'include'
        });

        if (!deleteResponse.ok) {
          const error = await deleteResponse.json();
          console.error('Brand deletion error:', error);
          // Brand silme hatasını göster ama devam et
          toast.error('Subscription cancelled but failed to delete brand');
        }
      }

      toast.success('Subscription cancelled and brand removed successfully');
      await fetchBrands(); // Brands listesini güncelle
      setIsCancelModalOpen(false);
      setSubscriptionToCancel(null);
      setIsBillingModalOpen(false); // Billing modal'ını da kapat
      
    } catch (error) {
      console.error('Cancel subscription error:', error);
      toast.error(error.message);
    }
  };

  // Şifre değiştirme fonksiyonu
  const handleChangePassword = async () => {
    if (!currentPassword) {
      toast.error('Current password is required');
      return;
    }

    if (!newPassword) {
      toast.error('New password is required');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordStrength.score < 4) {
      toast.error('Password does not meet security requirements');
      return;
    }

    if (currentPassword === newPassword) {
      toast.error('New password must be different from current password');
      return;
    }

    setIsChangingPassword(true);
    try {
      // Get dynamic API URL
      const isLocalhost = window.location.hostname === 'localhost';
      const isPlatformDomain = window.location.hostname === 'platform.glowytics.com';
      
      let apiUrl;
      if (isLocalhost) {
        apiUrl = 'http://localhost:5000';
      } else if (isPlatformDomain) {
        apiUrl = 'https://api.glowytics.com';
      } else {
        apiUrl = process.env.NODE_ENV === 'production' 
          ? 'https://api.glowytics.com' 
          : 'http://localhost:5000';
      }

      const response = await fetch(`${apiUrl}/api/auth/update-password`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to change password');
      }

      const data = await response.json();
      toast.success(data.message || 'Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordStrength({ score: 0, feedback: [] });
      setShowPasswords({ current: false, new: false, confirm: false });
      setIsProfileModalOpen(false);
      
    } catch (error) {
      console.error('Change password error:', error);
      toast.error(error.message);
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleInitiatePayment = async (brandId, planId) => {
    // Sadece modal aç, token isteme
    setCurrentBrandPaying(brandId);
    setPaymentData({ brandId, planId }); // Sadece ID'leri sakla
    
    // Modal açılmadan önce güncel kuru al
    await fetchExchangeRate();
    
    setIsNewCardModalOpen(true);
  };

  // PayTR Direct API ile ödeme
  const handleNewCardPayment = async () => {
    console.log('🚀 Pay Now button clicked!');
    console.log('Card Details:', cardDetails);
    
    if (!paymentData || !cardDetails.cc_owner || !cardDetails.card_number || !cardDetails.expiry_month || !cardDetails.expiry_year || !cardDetails.cvv) {
      console.error('❌ Missing card details:', {
        paymentData: !!paymentData,
        cc_owner: !!cardDetails.cc_owner,
        card_number: !!cardDetails.card_number,
        expiry_month: !!cardDetails.expiry_month,
        expiry_year: !!cardDetails.expiry_year,
        cvv: !!cardDetails.cvv
      });
      toast.error('Please fill all card details');
      return;
    }

    setIsProcessingNewCard(true);
    toast.loading('Getting payment token...');

    try {
        const isAddingCardOnly = !paymentData.planId;
        const endpoint = isAddingCardOnly ? '/payment/add-card-token' : `/payment/new-card-token`;
        
        // Promo kodu bilgisini de gönder
        const body = isAddingCardOnly ? {} : { 
          brandId: paymentData.brandId, 
          planId: paymentData.planId,
          promoCode: promoApplied ? promoCode : null,
          promoDiscount: promoApplied ? promoDiscount : null
        };

        console.log(`🚀 Requesting PayTR token from endpoint: ${endpoint}`, body);

        const response = await fetch(`${API_URL}${endpoint}`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.error || 'Failed to get payment token.');
        }

        console.log('🎉 PayTR Token received!', data.paymentData);
        toast.dismiss();
        toast.loading('Processing payment...');

        // PayTR'a direkt POST yapacağız
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = 'https://www.paytr.com/odeme';

        // Kart bilgilerini ekle
        const cardFields = {
          cc_owner: cardDetails.cc_owner,
          card_number: cardDetails.card_number,
          expiry_month: cardDetails.expiry_month,
          expiry_year: cardDetails.expiry_year,
          cvv: cardDetails.cvv,
          store_card: cardDetails.store_card ? '1' : '0'
        };

        // PayTR verilerini ekle
        const allFields = { ...data.paymentData, ...cardFields };
        
        console.log('📝 Form fields to be sent:', allFields);

        Object.keys(allFields).forEach(key => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = allFields[key];
          form.appendChild(input);
        });

        document.body.appendChild(form);
        
        console.log('🚀 Submitting form to PayTR...');
        form.submit();

    } catch (error) {
      console.error('❌ New card payment error:', error);
      toast.dismiss();
      toast.error(error.message);
    } finally {
      setIsProcessingNewCard(false);
    }
  };

  const handleRenew = (subscription, brandCtx) => {
    // Normalize required identifiers from either subscription or provided brand context
    const brandId = subscription?.brand?.id || brandCtx?.id || subscription?.brandId || null;
    const brandName = subscription?.brand?.name || brandCtx?.name || 'this brand';
    const planId = subscription?.plan?.id || subscription?.planId || null;
    
    // Plan bilgisini subscription'dan veya subscriptionPlans listesinden bul
    let planDisplayName = subscription?.plan?.displayName || subscription?.planName || '';
    let planPriceMonthly = subscription?.plan?.priceMonthly || null;
    
    // Eğer plan bilgisi eksikse, subscriptionPlans listesinden bul
    if (planId && (!planDisplayName || !planPriceMonthly)) {
      const foundPlan = subscriptionPlans.find(p => p.id === planId);
      if (foundPlan) {
        planDisplayName = planDisplayName || foundPlan.displayName;
        planPriceMonthly = planPriceMonthly || foundPlan.priceMonthly;
      }
    }

    console.log('🔄 handleRenew called:', { brandId, brandName, planId, planDisplayName, planPriceMonthly });

    // Keep a normalized object for modal usage
    setSubscriptionToRenew({
      ...subscription,
      brand: { id: brandId, name: brandName },
      plan: { id: planId, displayName: planDisplayName, priceMonthly: planPriceMonthly }
    });

    if (storedCards && storedCards.length > 0) {
      setIsRenewModalOpen(true);
    } else {
      if (brandId && planId) {
        handleInitiatePayment(brandId, planId);
      } else {
        console.error('❌ Missing info:', { brandId, planId, subscription, brandCtx });
        toast.error('Missing brand or plan information to proceed with payment.');
      }
    }
  };

  const handlePayWithSavedCard = async (ctoken) => {
    if (!subscriptionToRenew) return;

    toast.loading('Processing payment...');
    setIsProcessingPayment(true);
    setCurrentBrandPaying(subscriptionToRenew?.brand?.id || null);

    try {
        const response = await fetch(`${API_URL}/payment/new-card-token`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                brandId: subscriptionToRenew?.brand?.id,
                planId: subscriptionToRenew?.plan?.id,
                ctoken: ctoken // Kayıtlı kart token'ını gönderiyoruz
            })
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
            throw new Error(data.error || 'Failed to initiate payment.');
        }

        toast.dismiss();
        
        // Gelen datayla PayTR'a formu submit et
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = 'https://www.paytr.com/odeme';
        Object.keys(data.paymentData).forEach(key => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = data.paymentData[key];
            form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();

    } catch (error) {
        toast.dismiss();
        toast.error(error.message);
        console.error('Error paying with saved card:', error);
    } finally {
        setIsProcessingPayment(false);
        setCurrentBrandPaying(null);
        setIsRenewModalOpen(false);
        setSubscriptionToRenew(null);
    }
};

  // Memoized payment summary to avoid recalculating on every render
  const paymentSummary = useMemo(() => {
    if (!paymentData?.planId) return null;
    const plan = subscriptionPlans.find(p => p.id === paymentData.planId);
    if (!plan) return null;
    const totalPriceUSD = Number(plan.priceMonthly) || 0;
    let finalTotalUSD = totalPriceUSD;
    if (promoApplied && promoDiscount) {
      const discountValue = Number(promoDiscount.value) || 0;
      finalTotalUSD = promoDiscount.type === 'percent'
        ? totalPriceUSD * (1 - discountValue / 100)
        : totalPriceUSD - discountValue;
    }
    finalTotalUSD = Number(finalTotalUSD) || 0;
    const rate = exchangeRate || 35.50;
    const finalTotalTL = finalTotalUSD * rate;
    const vatRate = 0.20;
    const subtotalTL = finalTotalTL / (1 + vatRate);
    const vatAmountTL = finalTotalTL - subtotalTL;
    const formatTL = (num) => num.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const formatUSD = (num) => num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return { plan, totalPriceUSD, finalTotalUSD, rate, finalTotalTL, subtotalTL, vatAmountTL, formatTL, formatUSD };
  }, [paymentData?.planId, subscriptionPlans, promoApplied, promoDiscount, exchangeRate]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <h1 className="text-2xl font-semibold mb-6">Select Brand Account</h1>
        <div className="animate-pulse">
          <div className="h-32 bg-gray-200 rounded-lg mb-4"></div>
          <div className="h-32 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Logo Container */}
      <div className="p-8">
        <img
          src="/Calisma-Yuzeyi-20@5x-4.png"
          alt="Glowytics Logo"
          className="h-8"
        />
      </div>

      {/* User Menu */}
      <div className="absolute top-2 sm:top-4 right-2 sm:right-4">
        <div className="relative">
          <button
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors duration-200"
          >
            <User className="w-3 sm:w-4 h-3 sm:h-4" />
            <span className="hidden sm:inline">{user?.name || user?.email}</span>
            <span className="sm:hidden truncate max-w-[80px]">{(user?.name || user?.email)?.split(' ')[0]}</span>
          </button>

          {/* Dropdown Menu */}
          {isUserMenuOpen && (
            <div className="absolute right-0 mt-1 sm:mt-2 w-40 sm:w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-1">
              <button
                onClick={handleOpenProfileModal}
                className="flex items-center gap-2 w-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-50"
              >
                <Settings className="w-3 sm:w-4 h-3 sm:h-4" />
                My Profile
              </button>
              <button
                onClick={handleOpenBillingModal}
                className="flex items-center gap-2 w-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-50"
              >
                <CreditCard className="w-3 sm:w-4 h-3 sm:h-4" />
                Billing
              </button>
              <button
                onClick={logout}
                className="flex items-center gap-2 w-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-red-600 hover:bg-red-50"
              >
                <LogOut className="w-3 sm:w-4 h-3 sm:h-4" />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto pt-8 sm:pt-16 px-4 pb-12">
        {/* Hero Section */}
        <div className="relative mb-10 sm:mb-16">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-emerald-50 border border-emerald-200 rounded-full mb-6">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              <span className="text-sm font-medium text-emerald-700">14-day free trial • No card required</span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              <span className="text-gray-900">Your Marketing</span>
              <span className="block mt-1 text-emerald-600">Command Center</span>
            </h1>

            <p className="mt-4 text-base sm:text-lg text-gray-500 max-w-xl mx-auto">
              Select an existing brand or create a new one to unlock AI-powered analytics and campaign optimization.
            </p>

            {/* CTA Button */}
            <div className="mt-8">
              <button
                onClick={handleCreateNew}
                className="group inline-flex items-center gap-3 px-6 sm:px-8 py-3.5 sm:py-4 bg-gray-900 text-white rounded-xl font-medium text-sm sm:text-base transition-all duration-200 hover:bg-gray-800"
              >
                <Plus className="w-5 h-5" />
                <span>Create New Brand</span>
              </button>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        {brands.length > 0 && (
          <div className="text-center mb-6 sm:mb-8">
            <div className="inline-flex items-center gap-2 bg-blue-50 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full">
              <Building className="w-3 sm:w-4 h-3 sm:h-4 text-blue-600" />
              <span className="text-xs sm:text-sm font-medium text-blue-700">
                {brands.length} {brands.length === 1 ? 'Brand' : 'Brands'} Total
              </span>
            </div>
          </div>
        )}

        {/* Brands List */}
        <div className="space-y-3 sm:space-y-4 pb-8">
          {brands.map(brand => (
            <Card 
              key={brand.id}
              className="p-4 sm:p-6 lg:p-8 rounded-xl sm:rounded-2xl hover:shadow-lg transition-all duration-200 cursor-pointer group border border-gray-200 hover:border-blue-200 hover:bg-blue-50/20"
            >
              <div 
                onClick={() => handleBrandSelect(brand.id)}
                className="w-full h-full"
              >
                {/* Mobile Layout */}
                <div className="sm:hidden">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="bg-gray-100 p-2 rounded-lg group-hover:bg-blue-100 flex-shrink-0">
                      {brand.logoUrl ? (
                        <img 
                          src={brand.logoUrl} 
                          alt={brand.name} 
                          className="w-5 h-5 object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                      ) : null}
                      <Building 
                        className="w-5 h-5 text-gray-600 group-hover:text-blue-600" 
                        style={{ display: brand.logoUrl ? 'none' : 'block' }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-base text-gray-800 group-hover:text-blue-600 truncate">{brand.name}</h3>
                        {brand.id === DEMO_BRAND_ID ? (
                          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full font-medium flex-shrink-0">
                            Demo
                          </span>
                        ) : brand.subscription && (
                          <>
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium flex-shrink-0">
                              {brand.subscription.planName}
                            </span>
                            {(brand.subscription.status === 'active' || brand.subscription.status === 'enterprise') && (
                              <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium flex-shrink-0">
                                {brand.subscription.status === 'enterprise' ? 'Enterprise' : '✓ Active'}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">
                        {brand.industry || 'No industry'} • {brand.currency || 'USD'}
                      </p>
                      {brand.websiteUrl && (
                        <p className="text-xs text-gray-400 truncate">
                          {brand.websiteUrl.replace(/^https?:\/\//, '')}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Mobile Subscription Info - hidden for demo brand */}
                  {brand.subscription && brand.id !== DEMO_BRAND_ID && (
                    <div className="flex flex-col gap-2">
                      {/* AI Credits Mobile */}
                      {brand.subscription.aiCreditsRemaining !== null && (
                        <div className="bg-purple-50 px-3 py-2 rounded-lg border border-purple-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Zap className="w-4 h-4 text-purple-600" />
                              <span className="text-xs text-purple-600 font-medium">AI Credits</span>
                            </div>
                            <span className="text-sm font-semibold text-purple-700">
                              {brand.subscription.aiCreditsRemaining === 999999 
                                ? 'Unlimited' 
                                : brand.subscription.aiCreditsRemaining}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {/* Trial Status Mobile */}
                      {brand.subscription.status === 'trial' && brand.subscription.daysRemaining !== null && (
                        <div className={`px-3 py-2 rounded-lg border ${
                          brand.subscription.daysRemaining > 5 
                            ? 'bg-green-50 border-green-200' 
                            : brand.subscription.daysRemaining > 2
                              ? 'bg-yellow-50 border-yellow-200'
                              : 'bg-red-50 border-red-200'
                        }`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${
                                brand.subscription.daysRemaining > 5 
                                  ? 'bg-green-500' 
                                  : brand.subscription.daysRemaining > 2
                                    ? 'bg-yellow-500'
                                    : 'bg-red-500'
                              }`}></div>
                              <span className={`text-xs font-medium ${
                                brand.subscription.daysRemaining > 5 
                                  ? 'text-green-600' 
                                  : brand.subscription.daysRemaining > 2
                                    ? 'text-yellow-600'
                                    : 'text-red-600'
                              }`}>
                                Trial Status
                              </span>
                            </div>
                            <span className={`text-sm font-semibold ${
                              brand.subscription.daysRemaining > 5 
                                ? 'text-green-700' 
                                : brand.subscription.daysRemaining > 2
                                  ? 'text-yellow-700'
                                  : 'text-red-700'
                            }`}>
                              {brand.subscription.daysRemaining > 0 
                                ? `${brand.subscription.daysRemaining} days left` 
                                : 'Trial ended'}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {/* Mobile Activate Plan Button */}
                      {(brand.subscription.status === 'trial' || brand.subscription.status === 'expired') && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleInitiatePayment(brand.id, brand.subscription.planId);
                          }}
                          disabled={isProcessingPayment && currentBrandPaying === brand.id}
                          className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-semibold disabled:opacity-50 shadow-sm"
                        >
                          {isProcessingPayment && currentBrandPaying === brand.id ? 'Processing...' : 'Activate Plan'}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Desktop Layout */}
                <div className="hidden sm:grid sm:grid-cols-5 gap-6 items-center">
                  {/* Main content area (Left + Right) */}
                  <div className="col-span-5 flex items-center justify-between">
                    {/* Left Side: Logo, Brand Name, Status */}
                    <div className="flex items-center gap-4">
                      <div className="bg-gray-100 p-3 rounded-xl group-hover:bg-blue-100 flex-shrink-0">
                        {brand.logoUrl ? (
                          <img
                            src={brand.logoUrl}
                            alt={brand.name}
                            className="w-6 h-6 object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                          />
                        ) : null}
                        <Building
                          className="w-6 h-6 text-gray-600 group-hover:text-blue-600"
                          style={{ display: brand.logoUrl ? 'none' : 'block' }}
                        />
                      </div>
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-semibold text-lg text-gray-800 group-hover:text-blue-600 truncate">{brand.name}</h3>
                          {brand.id === DEMO_BRAND_ID ? (
                            <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full font-medium flex-shrink-0">
                              Demo
                            </span>
                          ) : brand.subscription && (
                            <>
                              <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-medium flex-shrink-0">
                                {brand.subscription.planName}
                              </span>
                              {(brand.subscription.status === 'active' || brand.subscription.status === 'enterprise') && (
                                <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full font-medium flex-shrink-0">
                                  {brand.subscription.status === 'enterprise' ? 'Enterprise' : '✓ Active'}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 truncate">
                          {brand.industry || 'No industry'} • {brand.currency || 'USD'}
                          {brand.websiteUrl && ` • ${brand.websiteUrl.replace(/^https?:\/\//, '')}`}
                        </p>
                      </div>
                    </div>

                    {/* Right Side: Badges, Date, and Button - hidden for demo brand */}
                    {brand.id !== DEMO_BRAND_ID && (
                    <div className="flex items-center gap-6">
                      {/* AI Credits Badge - Orijinal Tasarım */}
                      {brand.subscription && brand.subscription.aiCreditsRemaining !== null && (
                          <div className="bg-purple-50 px-3 py-2 rounded-lg border border-purple-200">
                              <div className="flex items-center gap-2">
                                  <Zap className="w-4 h-4 text-purple-600" />
                                  <div>
                                      <p className="text-xs text-purple-600 font-medium">AI Credits</p>
                                      <p className="text-sm font-semibold text-purple-700">
                                          {brand.subscription.aiCreditsRemaining === 999999
                                              ? 'Unlimited'
                                              : brand.subscription.aiCreditsRemaining}
                                      </p>
                                  </div>
                              </div>
                          </div>
                      )}

                      {/* Trial or Expired Status - show activate button */}
                      {brand.subscription && (brand.subscription.status === 'trial' || brand.subscription.status === 'expired') && (
                          <div className="text-center">
                              <button
                                  onClick={(e) => { e.stopPropagation(); handleInitiatePayment(brand.id, brand.subscription.planId); }}
                                  disabled={isProcessingPayment && currentBrandPaying === brand.id}
                                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold disabled:opacity-50 shadow-sm whitespace-nowrap"
                              >
                                  {isProcessingPayment && currentBrandPaying === brand.id ? 'Processing...' : 'Activate Plan'}
                              </button>
                              {brand.subscription.status === 'trial' && brand.subscription.trialEndsAt && (
                                  <p className="text-xs text-gray-500 mt-1">
                                      Trial ends on {new Date(brand.subscription.trialEndsAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                  </p>
                              )}
                              {brand.subscription.status === 'expired' && brand.subscription.currentPeriodEnd && (
                                  <p className="text-xs text-red-600 mt-1">
                                      Expired on {new Date(brand.subscription.currentPeriodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                  </p>
                              )}
                          </div>
                      )}

                      {/* Active/Past Due Status */}
                      {brand.subscription && (brand.subscription.status === 'active' || brand.subscription.status === 'past_due') && (
                          <div className="text-center">
                              <button
                                  onClick={(e) => { e.stopPropagation(); handleRenew(brand.subscription, brand); }}
                                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-semibold shadow-sm whitespace-nowrap"
                              >
                                  Pay for Next Period
                              </button>
                              {brand.subscription.currentPeriodEnd && (
                                  <p className="text-xs text-gray-500 mt-1">
                                      Next payment on {new Date(brand.subscription.currentPeriodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                  </p>
                              )}
                          </div>
                      )}
                    </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}

          {/* Empty state - Clean Design */}
          {brands.length === 0 && !loading && (
            <div>
              <div className="bg-white rounded-2xl p-8 sm:p-12 border border-gray-200 shadow-sm">
                <div className="text-center max-w-md mx-auto">
                  {/* Icon */}
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-900 rounded-2xl mb-6">
                    <Rocket className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3">
                    Launch Your First Brand
                  </h3>
                  
                  <p className="text-gray-500 mb-8">
                    Connect your ad accounts and unlock powerful analytics to optimize your marketing campaigns.
                  </p>
                  
                  {/* Features */}
                  <div className="flex items-center justify-center gap-6 mb-8 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-500" />
                      <span>Analytics</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-500" />
                      <span>AI Insights</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-500" />
                      <span>Multi-Platform</span>
                    </div>
                  </div>
                  
                  {/* CTA Button */}
                  <button
                    onClick={handleCreateNew}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-xl font-medium transition-all duration-200 hover:bg-emerald-700"
                  >
                    <Plus className="w-5 h-5" />
                    Get Started Free
                  </button>
                  
                  <p className="mt-4 text-xs text-gray-400">
                    14-day free trial • No credit card required
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* New "Apple-style" Subscription Flow */}
        <AnimatePresence>
          {isModalOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="fixed inset-0 bg-white/95 backdrop-blur-md flex flex-col items-center justify-center z-50 p-4 overflow-y-auto"
            >
              <div className="absolute top-6 left-6 z-20">
                <img
                  src="/Calisma-Yuzeyi-20@5x-4.png"
                  alt="Glowytics Logo"
                  className="h-8"
                />
              </div>

              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setTimeout(() => {
                    setCurrentStep(1);
                    setSelectedPlan(null);
                  }, 200);
                }}
                className="absolute top-6 right-6 text-gray-500 hover:text-gray-900 transition-colors p-2 hover:bg-gray-200/50 rounded-full z-20"
              >
                <X className="w-6 h-6" />
              </button>
              
              <div className="w-full max-w-5xl py-16 sm:py-8">
                <AnimatePresence mode="wait" custom={direction}>
                  {currentStep === 1 && (
                    <StepComponent key={1} direction={direction}>
                      <div className="text-center mb-6 sm:mb-8">
                        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">Choose Your Plan</h1>
                        <p className="text-sm sm:text-base text-gray-600">14-day free trial • No credit card required</p>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
                        {subscriptionPlans.map((plan) => {
                          return (
                          <div
                            key={plan.id}
                            onClick={() => plan.name !== 'enterprise' && setSelectedPlan(plan)}
                            className={`relative border rounded-xl p-5 transition-all duration-200 flex flex-col ${
                              plan.name === 'enterprise' 
                                ? 'cursor-default border-gray-200 bg-gray-50/50'
                                : `cursor-pointer ${
                                    selectedPlan?.id === plan.id
                                      ? 'border-blue-500 ring-2 ring-blue-100 bg-white shadow-sm'
                                      : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                                  }`
                            }`}
                          >
                            {plan.name === 'pro' && (
                              <div className="absolute -top-2.5 left-1/2 transform -translate-x-1/2">
                                <div className="bg-blue-600 text-white px-3 py-0.5 rounded-full text-xs font-medium">
                                  POPULAR
                                </div>
                              </div>
                            )}
                            
                            {/* Header */}
                            <div className="mb-4">
                              <h3 className="text-lg font-semibold text-gray-900">{plan.displayName}</h3>
                            </div>
                            
                            {/* Price */}
                            <div className="mb-5">
                              {plan.name === 'enterprise' ? (
                                <div className="text-xl font-semibold text-gray-900">Contact Sales</div>
                              ) : (
                                <div className="flex items-baseline gap-1">
                                  <span className="text-3xl font-bold text-gray-900">${plan.priceMonthly}</span>
                                  <span className="text-gray-500 text-sm">/ month</span>
                                </div>
                              )}
                            </div>
                            
                            {/* Features */}
                            <div className="space-y-2.5 mb-5 flex-grow">
                              <div className="flex items-start gap-2.5">
                                <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                                <span className="text-sm text-gray-600">
                                  {plan.maxTeamMembers === -1 ? 'Unlimited team members' : 
                                   plan.maxTeamMembers === 1 ? '1 team member' : `Up to ${plan.maxTeamMembers} team members`}
                                </span>
                              </div>
                              {plan.features.map((feature, featureIndex) => (
                                <div key={featureIndex} className="flex items-start gap-2.5">
                                  <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                                  <span className="text-sm text-gray-600">{formatFeature(feature)}</span>
                                </div>
                              ))}
                            </div>
                            
                            {/* CTA Button */}
                            <div className="mt-auto pt-2">
                              {plan.name === 'enterprise' ? (
                                <button className="w-full py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors">
                                  Contact Sales
                                </button>
                              ) : (
                                <div className={`w-full py-2.5 text-center rounded-lg text-sm font-medium transition-colors ${
                                    selectedPlan?.id === plan.id
                                      ? 'bg-gray-900 text-white'
                                      : plan.name === 'pro' && !selectedPlan
                                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                  }`}>
                                  {selectedPlan?.id === plan.id ? 'Selected' : 'Select Plan'}
                                </div>
                              )}
                            </div>
                          </div>
                        )})}
                      </div>
                      <div className="mt-6 sm:mt-8 text-center">
                        <button
                          onClick={() => {
                            if (selectedPlan) {
                              setDirection(1);
                              setCurrentStep(2);
                            } else {
                              toast.error('Please select a plan to continue');
                            }
                          }}
                          disabled={!selectedPlan}
                          className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-3.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-all duration-200 font-medium text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Continue <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                      </div>
                    </StepComponent>
                  )}
                  {currentStep === 2 && (
                    <StepComponent key={2} direction={direction}>
                      <div className="text-center">
                        <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-4">Tell us about your brand</h1>
                        <p className="text-lg text-gray-600">We'll use this to customize your experience.</p>
                      </div>
                      
                      <div className="mt-12 max-w-xl mx-auto">
                        <div className="bg-white/50 border border-gray-200 p-4 rounded-xl mb-8">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-500">Selected Plan</p>
                                    <p className="font-semibold text-gray-800">{selectedPlan?.displayName}</p>
                                </div>
                                <button onClick={() => { setDirection(-1); setCurrentStep(1); }} className="text-sm font-medium text-blue-600 hover:text-blue-800">Change</button>
                            </div>
                        </div>

                        <form onSubmit={(e) => {
                          e.preventDefault();
                          if (newBrandName.trim()) {
                            setDirection(1);
                            setCurrentStep(3);
                          } else {
                            toast.error('Please enter brand name');
                          }
                        }}>
                          <div className="space-y-4 text-left">
                              <div className="sm:col-span-2">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Brand Name *</label>
                                <input type="text" value={newBrandName} onChange={(e) => setNewBrandName(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/80" placeholder="e.g. Acme Corporation" required />
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Website URL</label>
                                    <input type="text" value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/80" placeholder="example.com" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Industry</label>
                                    <select value={industry} onChange={(e) => setIndustry(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/80">
                                        <option value="">Select E-commerce Vertical</option>
                                        <option value="Fashion & Apparel">Fashion & Apparel</option>
                                        <option value="Electronics & Gadgets">Electronics & Gadgets</option>
                                        <option value="Home & Garden">Home & Garden</option>
                                        <option value="Health & Beauty">Health & Beauty</option>
                                        <option value="Sports & Outdoors">Sports & Outdoors</option>
                                        <option value="Toys & Hobbies">Toys & Hobbies</option>
                                        <option value="Food & Beverage">Food & Beverage</option>
                                        <option value="Subscription Boxes">Subscription Boxes</option>
                                        <option value="Online Marketplace">Online Marketplace</option>
                                        <option value="Digital Products & Services">Digital Products & Services</option>
                                        <option value="Handmade & Artisan Goods">Handmade & Artisan Goods</option>
                                        <option value="Dropshipping">Dropshipping</option>
                                        <option value="Automotive">Automotive Parts & Accessories</option>
                                        <option value="Pet Supplies">Pet Supplies</option>
                                        <option value="Other E-commerce">Other E-commerce</option>
                                    </select>
                                </div>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Currency</label>
                                    <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/80">
                                        <option value="">Select currency</option>
                                        <option value="USD">USD - US Dollar</option>
                                        <option value="EUR">EUR - Euro</option>
                                        <option value="TRY">TRY - Turkish Lira</option>
                                        <option value="GBP">GBP - British Pound</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Time Zone</label>
                                    <select value={timeZone} onChange={(e) => setTimeZone(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/80">
                                        <option value="">Select time zone</option>
                                        <option value="UTC">UTC</option>
                                        <option value="Europe/Istanbul">Europe/Istanbul</option>
                                        <option value="Europe/London">Europe/London</option>
                                        <option value="America/New_York">America/New York</option>
                                        <option value="America/Los_Angeles">America/Los Angeles</option>
                                    </select>
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Brand Logo URL</label>
                                <input type="url" value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/80" placeholder="https://example.com/logo.png" />
                                <p className="text-xs text-gray-500 mt-1 pl-1">Optional: For better recognition in the dashboard.</p>
                              </div>
                          </div>
                          <div className="flex justify-between items-center mt-12">
                            <button type="button" onClick={() => { setDirection(-1); setCurrentStep(1); }} className="inline-flex items-center gap-2 px-6 py-3 text-gray-700 rounded-full hover:bg-gray-200/50 transition-colors font-medium">
                              <ArrowLeft className="w-5 h-5" /> Back
                            </button>
                            <button type="submit" className="inline-flex items-center gap-2 px-8 py-4 bg-gray-900 text-white rounded-full hover:shadow-2xl transition-all duration-300 shadow-lg font-semibold text-lg">
                              Review & Continue <ArrowRight className="w-5 h-5" />
                            </button>
                          </div>
                        </form>
                      </div>
                    </StepComponent>
                  )}
                  {currentStep === 3 && (
                     <StepComponent key={3} direction={direction}>
                        <div className="text-center">
                          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-4">Start Your Journey</h1>
                          <p className="text-lg text-gray-600">Review your details and start your 14-day free trial.</p>
                        </div>

                        <div className="mt-12 max-w-2xl mx-auto bg-white/50 border border-gray-200 rounded-2xl p-8 space-y-6 text-left">
                            {/* Plan Summary */}
                            <div>
                                <h3 className="font-semibold text-lg mb-3">Selected Plan</h3>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="font-medium text-lg">{selectedPlan?.displayName}</p>
                                        <p className="text-sm text-gray-600">{selectedPlan?.description}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-2xl font-bold text-blue-900">${selectedPlan?.priceMonthly}</p>
                                        <p className="text-blue-600 text-sm">per month</p>
                                    </div>
                                </div>
                            </div>
                            {/* Brand Summary */}
                            <div className="border-t border-gray-200 pt-6">
                                <h3 className="font-semibold text-lg mb-3">Brand Details</h3>
                                <div className="space-y-2">
                                    <p><span className="font-medium text-gray-500">Name:</span> {newBrandName}</p>
                                    {websiteUrl && <p><span className="font-medium text-gray-500">Website:</span> {websiteUrl}</p>}
                                    {industry && <p><span className="font-medium text-gray-500">Industry:</span> {industry}</p>}
                                    {currency && <p><span className="font-medium text-gray-500">Currency:</span> {currency}</p>}
                                    {timeZone && <p><span className="font-medium text-gray-500">Time Zone:</span> {timeZone}</p>}
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-between items-center mt-12 max-w-2xl mx-auto">
                            <button type="button" onClick={() => { setDirection(-1); setCurrentStep(2); }} className="inline-flex items-center gap-2 px-6 py-3 text-gray-700 rounded-full hover:bg-gray-200/50 transition-colors font-medium">
                                <ArrowLeft className="w-5 h-5" /> Back
                            </button>
                            <button onClick={handleCreateBrand} disabled={isCreating} className="inline-flex items-center gap-3 px-8 py-4 bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-all duration-300 shadow-lg font-semibold text-lg disabled:opacity-50">
                                {isCreating ? 'Starting Trial...' : 'Start Free Trial'}
                            </button>
                        </div>
                    </StepComponent>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Profile Modal */}
        {isProfileModalOpen && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-xl sm:rounded-2xl w-full max-w-sm sm:max-w-md shadow-2xl overflow-hidden border border-gray-200">
              {/* Header */}
              <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100 bg-gray-50">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900">My Profile</h2>
                  </div>
                  <button 
                    onClick={() => {
                      setIsProfileModalOpen(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                      setPasswordStrength({ score: 0, feedback: [] });
                      setShowPasswords({ current: false, new: false, confirm: false });
                    }}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                {/* User Info */}
                <div className="bg-gray-50 p-4 rounded-xl">
                  <h3 className="font-semibold text-gray-900 mb-3">Account Information</h3>
                  <div className="space-y-2">
                    <div>
                      <label className="text-sm text-gray-500">Name</label>
                      <p className="font-medium text-gray-900">{user?.name || 'No name set'}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">Email</label>
                      <p className="font-medium text-gray-900">{user?.email}</p>
                    </div>
                  </div>
                </div>

                {/* Password Change */}
                <div className="border-t pt-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Change Password</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Current Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswords.current ? 'text' : 'password'}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className="w-full px-3 py-2 pr-20 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter current password"
                          disabled={isChangingPassword}
                        />
                        <button
                          type="button"
                          className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                        >
                          {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswords.new ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="w-full px-3 py-2 pr-20 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter new password"
                          disabled={isChangingPassword}
                        />
                        <button
                          type="button"
                          className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                        >
                          {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      </div>

                      {/* Password Strength Indicator */}
                      {newPassword && (
                        <div className="mt-2 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-600">Password Strength</span>
                            <span className={`text-xs font-medium ${
                              passwordStrength.score < 2 ? 'text-red-600' :
                              passwordStrength.score < 4 ? 'text-yellow-600' : 'text-green-600'
                            }`}>
                              {passwordStrength.score < 2 ? 'Weak' :
                               passwordStrength.score < 4 ? 'Medium' : 'Strong'}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                              className={`h-1.5 rounded-full transition-all duration-300 ${
                                passwordStrength.score < 2 ? 'bg-red-500' :
                                passwordStrength.score < 4 ? 'bg-yellow-500' : 'bg-green-500'
                              }`}
                              style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                            ></div>
                          </div>
                          {passwordStrength.feedback.length > 0 && (
                            <div className="text-xs text-gray-500">
                              <span>Missing: </span>
                              {passwordStrength.feedback.join(', ')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Confirm New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswords.confirm ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="w-full px-3 py-2 pr-20 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Confirm new password"
                          disabled={isChangingPassword}
                        />
                        <button
                          type="button"
                          className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                        >
                          {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      </div>

                      {/* Password Match Indicator */}
                      {confirmPassword && (
                        <div className="mt-1">
                          {newPassword === confirmPassword ? (
                            <p className="text-xs text-green-600 flex items-center">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Passwords match
                            </p>
                          ) : (
                            <p className="text-xs text-red-600 flex items-center">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Passwords do not match
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-3 pt-4 border-t">
                  {/* Delete account (danger) */}
                  <button
                    onClick={async () => {
                      if (!window.confirm('Are you sure? This will permanently delete your account.')) return;
                      try {
                        const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://api.glowytics.com';
                        const resp = await fetch(`${apiBase}/api/auth/delete-account`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          credentials: 'include',
                          body: JSON.stringify({ confirm: true })
                        });
                        const data = await resp.json();
                        if (!resp.ok) throw new Error(data.error || 'Failed to delete account');
                        localStorage.clear();
                        window.location.href = '/login';
                      } catch (e) {
                        alert(e.message);
                      }
                    }}
                    className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm sm:text-base"
                  >
                    Delete my account
                  </button>
                  <button
                    onClick={() => {
                      setIsProfileModalOpen(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                      setPasswordStrength({ score: 0, feedback: [] });
                      setShowPasswords({ current: false, new: false, confirm: false });
                    }}
                    className="w-full sm:w-auto px-3 sm:px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleChangePassword}
                    disabled={!currentPassword || !newPassword || !confirmPassword || isChangingPassword || passwordStrength.score < 4 || newPassword !== confirmPassword}
                    className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base flex items-center justify-center gap-2"
                  >
                    {isChangingPassword ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Changing...
                      </>
                    ) : (
                      <>
                        <Lock className="w-4 h-4" />
                        Change Password
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Billing Modal */}
        {isBillingModalOpen && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-xl sm:rounded-2xl w-full max-w-sm sm:max-w-4xl max-h-[95vh] sm:max-h-[90vh] shadow-2xl overflow-hidden border border-gray-200">
              {/* Header */}
              <div className="px-6 py-5 border-b border-gray-100 bg-gray-50">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-emerald-600" />
                    </div>
                  <div>
                      <h2 className="text-xl font-semibold text-gray-900">Billing & Subscriptions</h2>
                      <p className="text-gray-500 text-sm">Manage your plans, payment methods, and history</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setIsBillingModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Tabs */}
                <div className="mt-4 flex gap-1 bg-gray-100 rounded-xl p-1">
                  <button
                    onClick={() => setBillingActiveTab('subscriptions')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      billingActiveTab === 'subscriptions'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    Subscriptions
                  </button>
                  <button
                    onClick={() => setBillingActiveTab('cards')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      billingActiveTab === 'cards'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    Payment Methods
                  </button>
                  <button
                    onClick={() => setBillingActiveTab('history')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      billingActiveTab === 'history'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    Payment History
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                {/* Subscriptions Tab */}
                {billingActiveTab === 'subscriptions' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Subscriptions</h3>
                      
                      {userSubscriptions.length === 0 ? (
                        <div className="bg-gray-50 rounded-xl p-8 text-center">
                          <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <h4 className="text-lg font-medium text-gray-900 mb-2">No Active Subscriptions</h4>
                          <p className="text-gray-500">You don't have any active subscriptions yet.</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {userSubscriptions.map((subscription) => (
                            <div key={subscription.id} className="border border-gray-200 rounded-lg p-4">
                              {/* Brand Name and Badges */}
                              <div className="flex items-center gap-3 mb-3">
                                <h4 className="font-semibold text-gray-900">
                                  {subscription.brand.name}
                                </h4>
                                <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                                  {subscription.plan.displayName}
                                </span>
                                <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                  subscription.status === 'trial'
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : (subscription.status === 'active' || subscription.status === 'enterprise')
                                      ? 'bg-green-100 text-green-700'
                                      : 'bg-red-100 text-red-700'
                                }`}>
                                  {subscription.status === 'enterprise' ? 'Enterprise' : (subscription.status === 'active' ? '✓ Active' : subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1))}
                                </span>
                              </div>
                              
                              {subscription.status === 'cancelled' ? (
                                // Cancelled subscriptions - simplified layout
                                <div className="flex items-center justify-between">
                                  <div>
                                    <label className="text-gray-500 text-sm">Monthly Price</label>
                                    <p className="font-medium">${subscription.plan.priceMonthly}/month</p>
                                  </div>
                                  <span className="text-sm text-gray-500">Subscription cancelled</span>
                                </div>
                              ) : (
                                // Active/Trial subscriptions - compact layout
                                <div>
                                  {/* Desktop Layout */}
                                  <div className="hidden sm:flex items-center justify-between">
                                    {/* Subscription Details */}
                                    <div className="flex items-center gap-8 text-sm">
                                      <div>
                                        <label className="text-gray-500">Monthly Price</label>
                                        <p className="font-medium">${subscription.plan.priceMonthly}/month</p>
                                      </div>
                                      
                                      {subscription.status === 'trial' && subscription.trialEndsAt && (
                                        <div>
                                          <label className="text-gray-500">Trial Ends</label>
                                          <p className="font-medium">
                                            {new Date(subscription.trialEndsAt).toLocaleDateString()}
                                          </p>
                                        </div>
                                      )}
                                      
                                      <div>
                                        <label className="text-gray-500">Next Payment</label>
                                        <p className="font-medium">
                                          {subscription.status === 'trial' && subscription.trialEndsAt
                                            ? new Date(new Date(subscription.trialEndsAt).getTime() + 24 * 60 * 60 * 1000).toLocaleDateString()
                                            : new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                                        </p>
                                      </div>
                                      
                                      <div>
                                        <label className="text-gray-500">AI Credits</label>
                                        <p className="font-medium">
                                          {subscription.aiCreditsRemaining === 999999 
                                            ? 'Unlimited' 
                                            : subscription.aiCreditsRemaining}
                                        </p>
                                      </div>
                                    </div>
                                    
                                    {/* Action Buttons */}
                                    <div className="flex items-center gap-3">
                                      {(subscription.status === 'trial' || subscription.status === 'expired' || subscription.status === 'suspended') && (
                                        <button
                                          onClick={() => handleInitiatePayment(subscription.brand.id, subscription.plan.id)}
                                          disabled={isProcessingPayment && currentBrandPaying === subscription.brand.id}
                                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-semibold disabled:opacity-50 shadow-sm whitespace-nowrap"
                                        >
                                          {isProcessingPayment && currentBrandPaying === subscription.brand.id ? 'Processing...' : 'Activate Plan'}
                                        </button>
                                      )}
                                      {subscription.status === 'trial' && subscription.trialEndsAt && (
                                        <span className="text-xs text-gray-500">
                                          Trial ends on {new Date(subscription.trialEndsAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        </span>
                                      )}
                                      {subscription.status === 'expired' && subscription.currentPeriodEnd && (
                                        <span className="text-xs text-red-600">
                                          Expired on {new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        </span>
                                      )}
                                      {subscription.status === 'active' && (
                                        <button
                                          onClick={() => handleRenew(subscription)}
                                          className="bg-green-600 text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-green-700 transition-colors"
                                        >
                                          Pay for Next Period
                                        </button>
                                      )}
                                      <button
                                        onClick={() => handleCancelSubscription(subscription.id, subscription.brand.name)}
                                        className="text-red-600 hover:text-red-700 text-sm font-medium"
                                      >
                                        Cancel Plan
                                      </button>
                                    </div>
                                  </div>

                                  {/* Mobile Layout */}
                                  <div className="sm:hidden">
                                    <div className="grid grid-cols-2 gap-4 mb-3 text-sm">
                                      <div>
                                        <label className="text-gray-500">Monthly Price</label>
                                        <p className="font-medium">${subscription.plan.priceMonthly}/month</p>
                                      </div>
                                      <div>
                                        <label className="text-gray-500">AI Credits</label>
                                        <p className="font-medium">
                                          {subscription.aiCreditsRemaining === 999999 
                                            ? 'Unlimited' 
                                            : subscription.aiCreditsRemaining}
                                        </p>
                                      </div>
                                      {subscription.status === 'trial' && subscription.trialEndsAt && (
                                        <div>
                                          <label className="text-gray-500">Trial Ends</label>
                                          <p className="font-medium">
                                            {new Date(subscription.trialEndsAt).toLocaleDateString()}
                                          </p>
                                        </div>
                                      )}
                                      <div>
                                        <label className="text-gray-500">Next Payment</label>
                                        <p className="font-medium">
                                          {subscription.status === 'trial' && subscription.trialEndsAt
                                            ? new Date(new Date(subscription.trialEndsAt).getTime() + 24 * 60 * 60 * 1000).toLocaleDateString()
                                            : new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                                        </p>
                                      </div>
                                    </div>
                                    
                                    <div className="flex gap-2">
                                      {(subscription.status === 'trial' || subscription.status === 'expired' || subscription.status === 'suspended') && (
                                        <button
                                          onClick={() => handleInitiatePayment(subscription.brand.id, subscription.plan.id)}
                                          disabled={isProcessingPayment && currentBrandPaying === subscription.brand.id}
                                          className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-semibold disabled:opacity-50 shadow-sm"
                                        >
                                          {isProcessingPayment && currentBrandPaying === subscription.brand.id ? 'Processing...' : 'Activate Plan'}
                                        </button>
                                      )}
                                      {subscription.status === 'trial' && subscription.trialEndsAt && (
                                        <p className="text-xs text-gray-500 w-full text-center">
                                          Trial ends on {new Date(subscription.trialEndsAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        </p>
                                      )}
                                      {subscription.status === 'expired' && subscription.currentPeriodEnd && (
                                        <p className="text-xs text-red-600 w-full text-center">
                                          Expired on {new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        </p>
                                      )}
                                      {subscription.status === 'active' && (
                                        <button
                                          onClick={() => handleRenew(subscription)}
                                          className="w-full bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                                        >
                                          Pay for Next Period
                                        </button>
                                      )}
                                      <button
                                        onClick={() => handleCancelSubscription(subscription.id, subscription.brand.name)}
                                        className="text-red-600 hover:text-red-700 text-sm font-medium mt-1 w-full text-center"
                                      >
                                        Cancel Plan
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Payment Methods Tab */}
                {billingActiveTab === 'cards' && (
                  <div className="space-y-6">
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Saved Payment Methods</h3>
                        <button 
                            onClick={() => handleInitiatePayment(null, null)} // Brand/Plan olmadan sadece kart ekleme modalını aç
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold shadow-sm"
                        >
                            + Add New Card
                        </button>
                      </div>
                      
                      {loadingCards ? (
                        <div className="bg-gray-50 rounded-xl p-8 text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                          <p className="text-gray-500">Loading your saved cards...</p>
                        </div>
                      ) : storedCards.length === 0 ? (
                        <div className="bg-gray-50 rounded-xl p-8 text-center">
                          <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <h4 className="text-lg font-medium text-gray-900 mb-2">No Saved Cards</h4>
                          <p className="text-gray-500 mb-4">
                            You don't have any saved payment methods yet. Cards will be automatically saved when you make your first payment.
                          </p>
                          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                            <div className="flex items-start gap-3">
                              <div className="bg-blue-100 p-2 rounded-lg">
                                <Shield className="w-5 h-5 text-blue-600" />
                              </div>
                              <div className="text-left">
                                <h5 className="font-semibold text-blue-900 mb-1">Secure Card Storage</h5>
                                <p className="text-blue-700 text-sm">
                                  Your payment information is securely stored by PayTR and encrypted with bank-level security. We never store your full card details on our servers.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {storedCards.map((card) => (
                            <div key={card.ctoken} className="border border-gray-200 rounded-lg p-4 bg-white">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                  <div className="bg-gray-100 p-3 rounded-lg">
                                    <CreditCard className="w-6 h-6 text-gray-600" />
                                  </div>
                                  <div>
                                    <div className="flex items-center gap-3 mb-1">
                                      <h4 className="font-semibold text-gray-900">
                                        {card.cardMask || '**** **** **** ****'}
                                      </h4>
                                      {card.cardBrand && (
                                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-full font-medium uppercase">
                                          {card.cardBrand}
                                        </span>
                                      )}
                                      {card.requireCvv && (
                                        <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full font-medium">
                                          CVV Required
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-sm text-gray-500">
                                      {card.cardType && `${card.cardType} • `}
                                      {card.lastUsedAt 
                                        ? `Last used ${new Date(card.lastUsedAt).toLocaleDateString()}` 
                                        : 'Never used'}
                                    </p>
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handleDeleteCard(card.ctoken)}
                                    className="px-3 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors text-sm font-medium border border-red-200"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Card Security Info */}
                    <div className="border-t pt-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Security & Privacy</h3>
                      <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                        <div className="flex items-start gap-3">
                          <div className="bg-green-100 p-2 rounded-lg">
                            <Shield className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-green-900 mb-2">Your Payment Security</h4>
                            <ul className="space-y-1 text-green-700 text-sm">
                              <li className="flex items-start gap-2">
                                <span className="text-green-500 mt-1">•</span>
                                <span>All card information is encrypted and stored securely by PayTR</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="text-green-500 mt-1">•</span>
                                <span>We never store your full card details on our servers</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="text-green-500 mt-1">•</span>
                                <span>First payment requires 3D Secure verification</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="text-green-500 mt-1">•</span>
                                <span>Recurring payments are processed without 3D Secure for convenience</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="text-green-500 mt-1">•</span>
                                <span>You can delete saved cards anytime</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Payment History Tab */}
                {billingActiveTab === 'history' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment History</h3>
                      
                      {paymentHistory.length === 0 ? (
                        <div className="bg-gray-50 rounded-xl p-8 text-center">
                          <div className="text-gray-400 mb-2">
                            <CreditCard className="w-8 h-8 mx-auto" />
                          </div>
                          <p className="text-gray-500">Payment history will appear here once you start making payments.</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {paymentHistory.map((payment) => (
                            <div key={payment.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                              <div className="flex items-center justify-between">
                                {/* Payment Info */}
                                <div className="flex-1">
                                  <div className="flex items-center gap-3 mb-2">
                                    <div className="bg-green-100 p-2 rounded-lg">
                                      <CreditCard className="w-4 h-4 text-green-600" />
                                    </div>
                                    <div>
                                      <h4 className="font-semibold text-gray-900">
                                        {payment.brand.name} - {payment.plan.displayName}
                                      </h4>
                                      <p className="text-sm text-gray-500">
                                        {payment.description || `${payment.plan.displayName} Plan Payment`}
                                      </p>
                                    </div>
                                  </div>
                                  
                                  {/* Payment Details - Mobile */}
                                  <div className="sm:hidden space-y-2 text-sm">
                                    <div className="flex justify-between">
                                      <span className="text-gray-500">Amount:</span>
                                      <span className="font-medium">${payment.amount} {payment.currency}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-500">Date:</span>
                                      <span className="font-medium">
                                        {new Date(payment.paymentDate).toLocaleDateString()}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-500">Method:</span>
                                      <span className="font-medium capitalize">
                                        {payment.paymentMethod?.replace('_', ' ') || 'Credit Card'}
                                      </span>
                                    </div>
                                    {payment.invoiceNumber && (
                                      <div className="flex justify-between">
                                        <span className="text-gray-500">Invoice:</span>
                                        <span className="font-medium">#{payment.invoiceNumber}</span>
                                      </div>
                                    )}
                                    {payment.providerTransactionId && (
                                      <div className="flex justify-between">
                                        <span className="text-gray-500">Transaction ID:</span>
                                        <span className="font-medium text-xs font-mono">
                                          {payment.providerTransactionId}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Payment Details - Desktop */}
                                <div className="hidden sm:flex items-center gap-8 text-sm">
                                  <div>
                                    <label className="text-gray-500">Amount</label>
                                    <p className="font-semibold text-lg">${payment.amount}</p>
                                  </div>
                                  
                                  <div>
                                    <label className="text-gray-500">Date</label>
                                    <p className="font-medium">
                                      {new Date(payment.paymentDate).toLocaleDateString()}
                                    </p>
                                  </div>
                                  
                                  <div>
                                    <label className="text-gray-500">Method</label>
                                    <p className="font-medium capitalize">
                                      {payment.paymentMethod?.replace('_', ' ') || 'Credit Card'}
                                    </p>
                                  </div>
                                  
                                  {payment.invoiceNumber && (
                                    <div>
                                      <label className="text-gray-500">Invoice</label>
                                      <p className="font-medium">#{payment.invoiceNumber}</p>
                                    </div>
                                  )}
                                  
                                  {payment.providerTransactionId && (
                                    <div>
                                      <label className="text-gray-500">Transaction ID</label>
                                      <p className="font-medium text-xs font-mono">
                                        {payment.providerTransactionId}
                                      </p>
                                    </div>
                                  )}
                                  
                                  <div className="flex items-center gap-2">
                                    <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium">
                                      ✓ Completed
                                    </span>
                                    
                                    {payment.invoiceUrl && (
                                      <a 
                                        href={payment.invoiceUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-800 text-xs underline"
                                      >
                                        View Invoice
                                      </a>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}



        {/* Cancel Subscription Confirmation Modal */}
        {isCancelModalOpen && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-xl sm:rounded-2xl w-full max-w-sm sm:max-w-md shadow-2xl overflow-hidden border border-gray-200">
              {/* Header */}
              <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100 bg-gray-50">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    </div>
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Cancel Subscription</h2>
                  </div>
                  <button 
                    onClick={() => {
                      setIsCancelModalOpen(false);
                      setSubscriptionToCancel(null);
                    }}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                {/* Warning Message */}
                <div className="text-center">
                  <div className="bg-red-50 p-4 rounded-2xl w-16 h-16 mx-auto mb-4 flex items-center justify-center border border-red-100">
                    <AlertTriangle className="w-8 h-8 text-red-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Are you sure you want to cancel?
                  </h3>
                  <p className="text-gray-600">
                    You're about to cancel the subscription for <strong>"{subscriptionToCancel?.brandName}"</strong>
                  </p>
                </div>

                {/* What will happen */}
                <div className="bg-red-50 p-4 rounded-xl border border-red-100">
                  <h4 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    What will happen when you cancel:
                  </h4>
                  <ul className="space-y-2 text-sm text-red-700">
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">•</span>
                      <span>The brand will be completely removed from your account</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">•</span>
                      <span>All API connections will be permanently disconnected</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">•</span>
                      <span>All campaign data and analytics will be permanently deleted</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">•</span>
                      <span>AI assistant access will be completely revoked</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">•</span>
                      <span>No further charges will be made</span>
                    </li>
                  </ul>
                </div>

                {/* Final Warning */}
                <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-200">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center">
                      <span className="text-yellow-800 text-xs font-bold">!</span>
                    </div>
                    <h4 className="font-semibold text-yellow-800">Important Notice</h4>
                  </div>
                  <p className="text-sm text-yellow-700">
                    This action <strong>cannot be undone</strong>. The entire brand and all its data will be permanently deleted. You'll need to create a new brand and subscription if you want to continue using our services.
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-4 border-t">
                  <button
                    onClick={handleConfirmCancelSubscription}
                    className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 text-gray-500 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors font-medium text-sm order-2 sm:order-1"
                  >
                    Yes, Delete Brand
                  </button>
                  <button
                    onClick={() => {
                      setIsCancelModalOpen(false);
                      setSubscriptionToCancel(null);
                    }}
                    className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium shadow-md order-1 sm:order-2"
                  >
                    Keep Subscription
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PayTR New Card Payment Modal */}
        {isNewCardModalOpen && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-md shadow-2xl overflow-hidden border border-gray-200">
              <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <CreditCard className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <h2 className="text-base font-semibold text-gray-900">Add Payment Method</h2>
                      <p className="text-gray-500 text-xs">Enter your card details</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => {
                      setIsNewCardModalOpen(false);
                      setCardDetails({
                        cc_owner: '',
                        card_number: '',
                        expiry_month: '',
                        expiry_year: '',
                        cvv: '',
                        store_card: true
                      });
                      setPaymentData(null);
                      setIsPromoOpen(false);
                      setPromoCode('');
                      setPromoApplied(false);
                      setPromoDiscount(null);
                    }}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 hover:bg-gray-100 rounded-lg"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
                {/* Payment Summary with VAT breakdown */}
                {paymentSummary && (
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 space-y-1.5">
                    {/* Plan info with USD price */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">
                        {paymentSummary.plan.displayName || 'Plan'}
                        <span className="text-gray-400 ml-1">/ month</span>
                      </span>
                      <span className="text-gray-500">${paymentSummary.formatUSD(paymentSummary.finalTotalUSD)}</span>
                    </div>

                    {/* Promo discount info */}
                    {promoApplied && promoDiscount && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-emerald-600 flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          Promo ({promoDiscount.type === 'percent' ? `${promoDiscount.value}%` : `$${promoDiscount.value}`})
                        </span>
                        <span className="text-gray-400 line-through">${paymentSummary.formatUSD(paymentSummary.totalPriceUSD)}</span>
                      </div>
                    )}

                    {/* Exchange rate info */}
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <span>Exchange Rate</span>
                      <span>1 USD = ₺{paymentSummary.formatTL(paymentSummary.rate)}</span>
                    </div>

                    {/* Subtotal TL */}
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>Subtotal</span>
                      <span>₺{paymentSummary.formatTL(paymentSummary.subtotalTL)}</span>
                    </div>

                    {/* VAT TL */}
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>VAT (20% included)</span>
                      <span>₺{paymentSummary.formatTL(paymentSummary.vatAmountTL)}</span>
                    </div>

                    {/* Total TL */}
                    <div className="flex items-center justify-between pt-1.5 border-t border-gray-200">
                      <span className="text-sm font-medium text-gray-700">Total</span>
                      <div className="text-right">
                        <span className="text-lg font-bold text-gray-900">₺{paymentSummary.formatTL(paymentSummary.finalTotalTL)}</span>
                        <span className="text-xs text-gray-400 ml-1">TL</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Card Owner & Number - Side by side on larger screens */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Card Holder</label>
                  <input
                    type="text"
                    value={cardDetails.cc_owner}
                    onChange={(e) => setCardDetails({...cardDetails, cc_owner: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-all bg-gray-50 focus:bg-white text-sm"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Card Number</label>
                  <input
                    type="text"
                    value={cardDetails.card_number}
                    onChange={(e) => setCardDetails({...cardDetails, card_number: e.target.value.replace(/\s/g, '')})}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-all bg-gray-50 focus:bg-white text-sm"
                    placeholder="1234 5678 9012 3456"
                    maxLength="16"
                  />
                  </div>
                </div>

                {/* Expiry Date & CVV */}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Month</label>
                    <select
                      value={cardDetails.expiry_month}
                      onChange={(e) => setCardDetails({...cardDetails, expiry_month: e.target.value})}
                      className="w-full px-2 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-all bg-gray-50 focus:bg-white text-sm"
                    >
                      <option value="">MM</option>
                      {MONTHS.map(({ value, label }) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Year</label>
                    <select
                      value={cardDetails.expiry_year}
                      onChange={(e) => setCardDetails({...cardDetails, expiry_year: e.target.value})}
                      className="w-full px-2 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-all bg-gray-50 focus:bg-white text-sm"
                    >
                      <option value="">YY</option>
                      {YEARS.map(({ value, label }) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">CVV</label>
                    <input
                      type="text"
                      value={cardDetails.cvv}
                      onChange={(e) => setCardDetails({...cardDetails, cvv: e.target.value})}
                      className="w-full px-2 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-all bg-gray-50 focus:bg-white text-sm"
                      placeholder="000"
                      maxLength="3"
                    />
                  </div>
                </div>

                {/* Promo Code - Compact Collapsible */}
                <div>
                  <button
                    type="button"
                    onClick={() => setIsPromoOpen(!isPromoOpen)}
                    className="text-xs text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-1"
                  >
                    <span>{isPromoOpen ? '−' : '+'}</span>
                    <span>Have a promo code?</span>
                  </button>
                  
                  {isPromoOpen && (
                    <div className="mt-2 flex gap-2">
                      <input
                        type="text"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                        disabled={promoApplied}
                        className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-all bg-gray-50 focus:bg-white text-sm uppercase tracking-wider disabled:bg-gray-100 disabled:text-gray-500"
                        placeholder="Enter code"
                      />
                      {promoApplied ? (
                        <button
                          type="button"
                          onClick={() => {
                            setPromoCode('');
                            setPromoApplied(false);
                            setPromoDiscount(null);
                          }}
                          className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm font-medium border border-red-200"
                        >
                          Remove
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            // Promo code validation
                            const code = promoCode.trim().toUpperCase();
                            if (!code) return;
                            
                            // Secret codes
                            const promoCodes = {
                              'GLOWY99': { type: 'percent', value: 99 },
                              'FOUNDER': { type: 'percent', value: 99 },
                              'WELCOME10': { type: 'percent', value: 10 },
                              'SAVE20': { type: 'percent', value: 20 },
                            };
                            
                            if (promoCodes[code]) {
                              setPromoApplied(true);
                              setPromoDiscount(promoCodes[code]);
                            } else {
                              // Invalid code
                              toast.error('Invalid promo code');
                            }
                          }}
                          disabled={!promoCode.trim()}
                          className="px-3 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Apply
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Store Card & Security - Combined compact row */}
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <input
                    type="checkbox"
                    id="store_card"
                    checked={cardDetails.store_card}
                    onChange={(e) => setCardDetails({...cardDetails, store_card: e.target.checked})}
                    className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500 border-gray-300 flex-shrink-0"
                  />
                  <label htmlFor="store_card" className="text-xs text-gray-600 flex-1">
                    <span className="font-medium text-gray-700">Save card for future</span>
                    <span className="text-gray-400 ml-1">• Securely stored by PayTR</span>
                  </label>
                  <img 
                    src="/PayTR-2025-New-Logo-Color.webp" 
                    alt="PayTR" 
                    className="h-5 object-contain opacity-70"
                  />
                </div>

                {/* Security Badge - Minimal */}
                <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
                  <Lock className="w-3 h-3" />
                  <span>Protected by 3D Secure</span>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => {
                      setIsNewCardModalOpen(false);
                      setCardDetails({
                        cc_owner: '',
                        card_number: '',
                        expiry_month: '',
                        expiry_year: '',
                        cvv: '',
                        store_card: true
                      });
                      setPaymentData(null);
                      setIsPromoOpen(false);
                      setPromoCode('');
                      setPromoApplied(false);
                      setPromoDiscount(null);
                    }}
                    className="flex-1 px-4 py-2.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors font-medium border border-gray-200 text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleNewCardPayment}
                    disabled={isProcessingNewCard}
                    className="flex-1 px-4 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm"
                  >
                    {isProcessingNewCard ? 'Processing...' : 'Pay Now'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Kart Silme Onay Modalı */}
      {cardToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
              <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
              >
                  <div className="p-6 text-center">
                      <div className="flex justify-center mb-4">
                          <AlertTriangle className="w-12 h-12 text-red-500" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Are you sure?</h3>
                      <p className="text-gray-600 mb-6">
                          You are about to delete the card ending in <span className="font-bold">{cardToDelete.cardMask.slice(-4)}</span>. This action cannot be undone.
                      </p>
                      <div className="flex justify-center gap-4">
                          <button
                              onClick={() => setCardToDelete(null)}
                              className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                          >
                              Cancel
                          </button>
                          <button
                              onClick={confirmDeleteCard}
                              className="px-6 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg font-medium transition-colors"
                          >
                              Yes, Delete Card
                          </button>
                      </div>
                  </div>
              </motion.div>
          </div>
      )}

      {/* Diğer Modallar */}

      {/* Kayıtlı Kart ile Yenileme Modalı */}
      {isRenewModalOpen && subscriptionToRenew && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
              <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="bg-white rounded-2xl w-full max-w-md shadow-2xl"
              >
                  <div className="p-6">
                      <div className="flex justify-between items-center mb-4">
                          <h3 className="text-xl font-semibold text-gray-900">Renew with Saved Card</h3>
                          <button onClick={() => setIsRenewModalOpen(false)} className="text-gray-500 hover:text-gray-800">
                              <X size={20} />
                          </button>
                      </div>
                      <p className="text-gray-600 mb-6">
                          Select a card to pay for the next period of <span className="font-semibold">{(subscriptionToRenew && subscriptionToRenew.brand && subscriptionToRenew.brand.name) ? subscriptionToRenew.brand.name : 'this brand'}</span>.
                      </p>
                      <div className="space-y-3">
                          {storedCards.map(card => (
                            <div key={card.ctoken} className="flex items-center justify-between p-3 border rounded-lg">
                              <div className="flex items-center gap-3">
                                <img src={card.logo} alt={card.brand} className="w-8 h-8" />
                                <div>
                                  <div className="font-semibold">{card.brand} •••• {card.last4}</div>
                                  <div className="text-xs text-gray-500">Exp {card.exp_month}/{card.exp_year}</div>
                                </div>
                              </div>
                              <button
                                  onClick={() => handlePayWithSavedCard(card.ctoken)}
                                  className="px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 transition-colors"
                              >
                                  Pay ${subscriptionToRenew && subscriptionToRenew.plan && subscriptionToRenew.plan.priceMonthly ? subscriptionToRenew.plan.priceMonthly : ''}
                              </button>
                            </div>
                          ))}
                      </div>
                  </div>
              </motion.div>
          </div>
      )}
    </div>
  );
}  